import { useTranslation } from 'react-i18next';

import type { Language } from '@/i18n/language';
import { useLanguage } from '@/i18n/use-language';
import { Button } from './ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from './ui/dropdown-menu';

/** Flag emoji per language. English uses the UK flag by product choice. */
const LANGUAGE_FLAGS: Record<Language, string> = {
	en: '🇬🇧',
	de: '🇩🇪',
};

/** Flag + code dropdown next to the theme toggle. Both headers render it. */
export function LanguageSwitcher() {
	const { t } = useTranslation();
	const { language, setLanguage, languages } = useLanguage();
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="relative h-9 gap-1.5 rounded-full bg-muted/30 border border-border px-2.5 shadow-sm hover:bg-muted/50 transition-colors"
					title={t('common.language.label')}
				>
					<span aria-hidden="true" className="text-base leading-none">
						{LANGUAGE_FLAGS[language]}
					</span>
					<span aria-hidden="true" className="text-xs font-medium text-muted-foreground">
						{language.toUpperCase()}
					</span>
					<span className="sr-only">{t('common.language.label')}</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-40">
				<DropdownMenuRadioGroup value={language} onValueChange={(value) => setLanguage(value as typeof language)}>
					{languages.map((code) => (
						<DropdownMenuRadioItem key={code} value={code} className="cursor-pointer gap-2">
							<span aria-hidden="true" className="text-base leading-none">
								{LANGUAGE_FLAGS[code]}
							</span>
							{t(`common.language.${code}`)}
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
